import { Chip, Divider, Stack, Typography } from '@mui/material';
import type { FsxBackupData } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { MultiAccordionsItem } from '@/components/accordion/multiAccordionsItem';

import { OtherSnapshotsSummary } from './otherSnapshotsSummary';

import { SnapshotSelectionEmptyState } from '../snapshotSelection/snapshotSelectionEmptyState';

const EON_TAG_PREFIX = 'eon:';
const EON_SERVICE_TAG = 'eon:service';

const isEonBackup = (backup: FsxBackupData): boolean => {
  if (!backup.tags) return false;
  // Check if any key in tags matches the eon:service tag
  for (const key of Object.keys(backup.tags)) {
    if (key === EON_SERVICE_TAG) return true;
  }
  return false;
};

const filterNonEonTags = (
  tags: Record<string, string> | undefined
): Record<string, string> => {
  if (!tags) return {};
  return Object.fromEntries(
    Object.entries(tags).filter(([key]) => !key.startsWith(EON_TAG_PREFIX))
  );
};

const formatBytes = (bytes?: number): string => {
  if (bytes === undefined || bytes === null) return 'Unknown';
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);

  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
};

const formatSnapshotStatus = (lifecycle?: string): string => {
  switch (lifecycle) {
    case 'AVAILABLE':
      return 'Available';
    case 'CREATING':
      return 'Creating';
    case 'TRANSFERRING':
      return 'Transferring';
    case 'DELETED':
      return 'Deleted';
    case 'FAILED':
      return 'Failed';
    case 'PENDING':
      return 'Pending';
    case 'COPYING':
      return 'Copying';
    default:
      return lifecycle || 'Unknown';
  }
};

interface FsxBackupListProps {
  backups: FsxBackupData[];
  selectedOrigin?: 'eon' | 'other';
}

export const FsxBackupList = ({
  backups,
  selectedOrigin = 'other',
}: FsxBackupListProps) => {
  // Filter backups based on selected origin:
  // - 'eon': show Eon-tagged backups (created by Eon)
  // - 'other': show non-Eon backups (not created by Eon)
  const displayBackups = backups.filter((b) =>
    selectedOrigin === 'eon' ? isEonBackup(b) : !isEonBackup(b)
  );

  const [selectedBackupId, setSelectedBackupId] = useState<string | undefined>(
    displayBackups[0]?.backupId
  );

  // Match AWS/Azure pattern: auto-select first item when list changes
  useEffect(() => {
    setSelectedBackupId(displayBackups[0]?.backupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backups, selectedOrigin]);

  if (!backups.length) {
    return (
      <Stack marginY='40px'>
        <SnapshotSelectionEmptyState description="Couldn't find any FSx backups for this date" />
      </Stack>
    );
  }

  if (!displayBackups.length) {
    return (
      <Stack marginY='40px'>
        <SnapshotSelectionEmptyState
          description={
            selectedOrigin === 'eon'
              ? 'No Eon-created FSx backups found for this date'
              : 'No other FSx backups found'
          }
        />
      </Stack>
    );
  }

  return (
    <Stack width='100%'>
      {displayBackups.map((backup) => {
        const displayTags = filterNonEonTags(backup.tags);

        return (
          <div key={backup.backupId}>
            <MultiAccordionsItem
              isSelected={selectedBackupId === backup.backupId}
              onSelect={() => setSelectedBackupId(backup.backupId)}
              isMulti={displayBackups.length > 1}
              summary={
                <OtherSnapshotsSummary
                  isMultiTimes={displayBackups.length > 1}
                  time={backup.creationTime || new Date()}
                  customLabel='FSx Backup'
                />
              }
              Details={
                <Stack marginLeft='212px' gap='16px'>
                  <Stack
                    direction='row'
                    gap='24px'
                    flexWrap='wrap'
                    divider={<Divider orientation='vertical' flexItem />}
                  >
                    <Stack>
                      <Typography variant='caption' color='text.secondary'>
                        Backup ID
                      </Typography>
                      <Typography variant='body2'>{backup.backupId}</Typography>
                    </Stack>
                    <Stack>
                      <Typography variant='caption' color='text.secondary'>
                        Status
                      </Typography>
                      <Typography variant='body2'>
                        {formatSnapshotStatus(backup.lifecycle)}
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant='caption' color='text.secondary'>
                        Size
                      </Typography>
                      <Typography variant='body2'>
                        {formatBytes(backup.size)}
                      </Typography>
                    </Stack>
                  </Stack>
                  {Object.keys(displayTags).length > 0 && (
                    <Stack gap='8px'>
                      <Typography variant='caption' color='text.secondary'>
                        Tags
                      </Typography>
                      <Stack direction='row' gap='8px' flexWrap='wrap'>
                        {Object.entries(displayTags).map(([key, value]) => (
                          <Chip
                            key={key}
                            label={`${key}: ${value}`}
                            size='small'
                            variant='outlined'
                          />
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              }
            />
            <Divider />
          </div>
        );
      })}
    </Stack>
  );
};
